/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Core, CollectionReturnValue, EdgeSingular } from 'cytoscape';

interface Props {
  cy: Core;
  source: CollectionReturnValue;
  targets: CollectionReturnValue;
}

interface PathProps extends Props {
  maxDepth?: number;
}

export function findPathsBetween({
  cy,
  source,
  targets,
}: Props): CollectionReturnValue {
  const collected = {
    value: [] as string[][],
  };
  const paths = findAllPaths({ cy, source, targets });
  paths.forEach((path) => {
    const nodeIds = path.nodes.map((x) => x.id());
    const edgeIds = path.edges.map((x) => x.id());
    collected.value.push(nodeIds);
    collected.value.push(edgeIds);
  });

  const uniqueIds = Array.from(new Set(collected.value.flatMap((e) => e)));
  const items = uniqueIds.map((id) => cy.getElementById(id));
  return cy.collection(items);
}

// finds all simple paths between source and target nodes
export function findAllPaths({ cy, source, targets, maxDepth }: PathProps): {
  nodes: CollectionReturnValue;
  edges: CollectionReturnValue;
  targetId: string;
}[] {
  const maxDepthValue = maxDepth || Infinity;
  const paths: {
    nodes: CollectionReturnValue;
    edges: CollectionReturnValue;
    targetId: string;
  }[] = [];

  function dfs(
    current: CollectionReturnValue,
    pathNodes: CollectionReturnValue,
    pathEdges: CollectionReturnValue,
    visited: Set<string>,
    depth: number
  ): void {
    if (depth > maxDepthValue) return;

    // Check if current node is a target: save the path but continue exploration
    if (targets.contains(current)) {
      paths.push({
        nodes: pathNodes.clone(), // Clone to avoid reference issues
        edges: pathEdges.clone(),
        targetId: current.id(), // Track which target was reached
      });
      // Do not return here: continue to explore further for longer paths to other targets
    }

    // Iterate over neighbors (undirected graph, bidirectional edges)
    current
      .neighborhood()
      .nodes()
      .forEach((neighbor: CollectionReturnValue) => {
        if (!visited.has(neighbor.id())) {
          // Get the edge connecting current to neighbor
          const edge: EdgeSingular = current
            .edgesWith(neighbor)
            .first() as EdgeSingular;
          const newPathNodes = pathNodes.add(neighbor);
          const newPathEdges = pathEdges.add(edge);
          const newVisited = new Set(visited).add(neighbor.id());

          dfs(neighbor, newPathNodes, newPathEdges, newVisited, depth + 1);
        }
      });
  }

  const initialVisited = new Set<string>([source.id()]);
  dfs(source, cy.collection().add(source), cy.collection(), initialVisited, 1);

  return paths;
}
